'use client';

// Original logic preserved · visual redesigned per Olga DESIGN-SYSTEM.md
// LEGACY-EKA-L0.5 (2026-04-08) + EKA-001/003: Multi-tenant Google OAuth + email/password
// + audit logging + rate-limit (5 fail / 15min / IP)

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Icon } from '@/components/ui/icon';
import { createClient } from '@/lib/supabase/client';

const ERROR_COPY: Record<string, string> = {
  unauthorized_domain: 'Twoja domena nie jest dozwolona w tej organizacji. Skontaktuj się z administratorem.',
  missing_env: 'Konfiguracja środowiska niekompletna — skontaktuj się z administratorem.',
  callback_failed: 'Logowanie nie powiodło się. Spróbuj ponownie.',
  oauth_failed: 'Logowanie Google nie powiodło się. Spróbuj ponownie.',
  rate_limited: 'Zbyt wiele prób logowania. Spróbuj ponownie za 15 minut.',
  invalid_credentials: 'Nieprawidłowy email lub hasło.',
};

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState('');

  const next = searchParams.get('next') || searchParams.get('redirectTo') || '/dashboard';
  const errorCode = searchParams.get('error');

  useEffect(() => {
    if (errorCode && ERROR_COPY[errorCode]) {
      setError(ERROR_COPY[errorCode]);
    }
  }, [errorCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const rl = await fetch('/api/auth/log-attempt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phase: 'pre' }),
      });
      if (rl.status === 429) {
        setError(ERROR_COPY.rate_limited);
        return;
      }

      const supabase = createClient();
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });

      if (signInErr) {
        await fetch('/api/auth/log-attempt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, phase: 'fail', reason: signInErr.message }),
        });
        setError(
          signInErr.message.toLowerCase().includes('invalid')
            ? ERROR_COPY.invalid_credentials
            : signInErr.message
        );
        return;
      }

      router.push(next);
      router.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Błąd logowania.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setOauthLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const redirectTo = `${origin}/auth/callback?next=${encodeURIComponent(next)}`;
      const { error: oauthErr } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: { prompt: 'select_account', access_type: 'offline' },
        },
      });
      if (oauthErr) throw oauthErr;
    } catch (err) {
      const message = err instanceof Error ? err.message : ERROR_COPY.oauth_failed;
      setError(message);
      setOauthLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Zaloguj się</h2>
        <p className="mt-1.5 text-sm text-slate-500">
          Email + hasło lub Google. Konta tworzone przez administratora kancelarii.
        </p>
      </div>

      {error && (
        <div
          className="rounded-lg bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700 flex items-start gap-2"
          role="alert"
        >
          <Icon name="error" variant="outline" size="md" className="text-red-500 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Google OAuth · secondary button (white border) */}
      <button
        type="button"
        disabled={loading || oauthLoading}
        onClick={handleGoogleSignIn}
        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-micro disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {oauthLoading ? (
          <>
            <Icon name="loading" variant="outline" size="md" className="animate-spin text-slate-400" />
            Przekierowanie do Google...
          </>
        ) : (
          <>
            {/* Google G logo · SVG inline */}
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#4285F4" d="M16.51 8.18c0-.56-.05-1.1-.15-1.62H9v3.06h4.21c-.18.95-.74 1.75-1.57 2.29v1.9h2.54c1.49-1.37 2.33-3.4 2.33-5.63z"/>
              <path fill="#34A853" d="M9 17c2.12 0 3.9-.7 5.2-1.91l-2.54-1.9c-.7.47-1.6.75-2.66.75-2.04 0-3.77-1.38-4.39-3.24H2v2.03A8 8 0 0 0 9 17z"/>
              <path fill="#FBBC04" d="M4.61 10.7A4.78 4.78 0 0 1 4.36 9c0-.59.1-1.16.25-1.7V5.27H2A8 8 0 0 0 1 9c0 1.29.3 2.5.99 3.73l2.62-2.03z"/>
              <path fill="#EA4335" d="M9 4.07c1.15 0 2.18.4 2.99 1.17l2.24-2.24A7.98 7.98 0 0 0 9 1a8 8 0 0 0-7 4.27L4.61 7.3C5.23 5.44 6.97 4.07 9 4.07z"/>
            </svg>
            Zaloguj się z Google
          </>
        )}
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-slate-400 uppercase tracking-wider font-medium">
            lub email + hasło
          </span>
        </div>
      </div>

      {/* Email input · Olga §5 search-input pattern adapted */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="twoj@kancelaria.pl"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading || oauthLoading}
          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 transition-colors focus:outline-none focus:border-[#239D9F] focus:ring-2 focus:ring-[#239D9F]/15 disabled:bg-slate-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Password input */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
          Hasło
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          placeholder="Twoje hasło"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading || oauthLoading}
          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 transition-colors focus:outline-none focus:border-[#239D9F] focus:ring-2 focus:ring-[#239D9F]/15 disabled:bg-slate-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Primary CTA · brand-bright */}
      <button
        type="submit"
        disabled={loading || oauthLoading}
        className="w-full px-4 py-2.5 bg-[#239D9F] text-white rounded-lg text-sm font-medium hover:bg-[#1E8A8C] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Icon name="loading" variant="outline" size="md" className="animate-spin" />
            Logowanie...
          </>
        ) : (
          'Zaloguj się'
        )}
      </button>

      {/* Secondary links */}
      <div className="space-y-1.5 text-center text-xs text-slate-500">
        <p>
          <Link
            href="/auth/forgot-password"
            className="text-[#239D9F] hover:text-[#1E8A8C] font-medium transition-micro"
          >
            Zapomniałeś hasła?
          </Link>
        </p>
        <p>
          Brak konta?{' '}
          <Link
            href="/signup"
            className="text-[#11305A] hover:text-[#239D9F] font-medium transition-micro"
          >
            Załóż kancelarię
          </Link>
        </p>
      </div>
    </form>
  );
}
